import type { Theme } from "@emotion/react";
import { type Shadows, type ThemeOptions, createTheme } from "@mui/material/styles";
import { colors } from "Infrastructure/Themes/Colors";

export type ColorsType = {
  BrandPrimary: string;
  BrandMedium: string;
  BrandLight: string;
  BrandLighter: string;
  BrandLightBorder: string;
  SuccessPrimary: string;
  SuccessMedium: string;
  SuccessLight: string;
  InfoPrimary: string;
  InfoMedium: string;
  InfoLight: string;
  InfoSuperLight: string;
  TextPrimary: string;
  TextSecondary: string;
  TextTertiary: string;
  TextDisabled: string;
  TextHint: string;
  TextInverse: string;
  BackgroundLight: string;
  BackgroundSecondary: string;
  BackgroundTertiary: string;
  Border: string;
  BorderLight: string;
  BorderDisabled: string;
  PrimaryHover: string;
  PrimaryDisabled: string;
  White: string;
  Black: string;
  Green: string;
  DangerDark: string;
  DangerPrimary: string;
  DangerLight: string;
  Warning: string;
  WarningLight: string;
  WarningMedium: string;
  Gray: string;
  DarkGrey: string;
  Placeholder: string;
  Validation: string;
  FieldBackground: string;
  ContentWrapperBorder: string;
  RadioBtnDisabled: string;
  WorkflowIcon: string;
  Body: string;
  ChartPositive: string;
  ChartPositiveLight: string;
  ChartPositiveLighter: string;
  ChartNeutral: string;
  ChartNeutralLight: string;
  ChartNeutralLighter: string;
  ChartNegative: string;
};

export enum Spacing {
  XS = 0.5,
  S = 1,
  M = 2,
  L = 3,
  XL = 4,
  XXL = 6,
  XXXL = 8,
  MAX = 100,
}

export enum Radius {
  S = 1,
  M = 2,
  L = 3,
  XL = 4,
  XXL = 5,
  XXXL = 6,
  MAX = 100,
}

export const MOBILE_MAX_WIDTH = 768;
export const TABLET_MAX_WIDTH = 820;

const theme = (color: ColorsType): Theme => {
  const theme: ThemeOptions = {
    palette: {
      primary: {
        light: color.BrandLight,
        main: color.BrandPrimary,
        dark: color.BrandMedium,
        contrastText: color.White,
      },
      secondary: {
        light: color.BorderLight,
        main: color.TextSecondary,
      },
      text: {
        primary: color.TextPrimary,
        secondary: color.TextSecondary,
        disabled: color.TextDisabled,
      },
      error: {
        main: color.DangerPrimary,
        light: color.DangerLight,
        dark: color.DangerDark,
        contrastText: color.White,
      },
      success: {
        main: color.Green,
        light: color.SuccessLight,
        dark: color.SuccessMedium,
      },
      info: {
        main: color.InfoPrimary,
        light: color.InfoLight,
        dark: color.InfoMedium,
        contrastText: color.White,
      },
      background: {
        default: color.Body,
      },
      common: {
        black: color.Black,
        white: color.White,
      },
      warning: {
        main: color.Warning,
        light: color.WarningLight,
        dark: color.WarningMedium,
      },
      divider: color.Border,
    },
    breakpoints: {
      values: {
        xs: 0,
        sm: 600,
        md: 1050,
        lg: 1280,
        xl: 1536,
      },
    },
    spacing: 4,
    shape: {
      borderRadius: 4,
    },
    typography: {
      h1: {
        color: color.TextPrimary,
        fontSize: 32,
        lineHeight: 1.4,
        fontWeight: 700,
        fontFamily: "Fedra Sans Pro",
      },
      h2: {
        color: color.TextPrimary,
        fontSize: 24,
        lineHeight: 1.4,
        fontWeight: 700,
        fontFamily: "Fedra Sans Pro",
      },
      h3: {
        color: color.TextPrimary,
        fontSize: 20,
        lineHeight: 1.4,
        fontWeight: 700,
        fontFamily: "Fedra Sans Pro",
      },
      subtitle1: {
        // Subheadline
        fontWeight: 400,
        color: color.TextPrimary,
        fontSize: 18,
        lineHeight: 1.3,
        letterSpacing: "0.2px",
        fontFamily: "Roboto",
      },
      subtitle2: {
        fontWeight: 600,
        color: color.TextPrimary,
        fontSize: 16,
        lineHeight: 1,
        letterSpacing: "0.2px",
        fontFamily: "Roboto",
        textTransform: "uppercase",
      },
      body1: {
        // Body MD
        color: color.TextPrimary,
        fontSize: 16,
        fontWeight: 400,
        lineHeight: 1.3,
        fontFamily: "Roboto",
      },
      body2: {
        // Body SM
        color: color.TextPrimary,
        fontSize: 14,
        fontWeight: 400,
        lineHeight: 1.3,
        fontFamily: "Roboto",
      },
      caption: {
        // Body SM
        color: color.TextPrimary,
        fontSize: 12,
        fontWeight: 400,
        lineHeight: 1.3,
        fontFamily: "Roboto",
      },
      overline: {
        // Body SM
        color: color.TextPrimary,
        fontSize: 11,
        fontWeight: 400,
        lineHeight: 1.3,
        fontFamily: "Roboto",
      },
    },
    shadows: [
      "none",
      "0px 2px 4px 0px #0307120A",
      "0px 2px 8px 0px #0307121A",
      "0px 4px 8px 0px #03071214",
      "0px 8px 16px 0px #03071214",
      "0px 16px 32px 0px #03071214",
      ...Array<string>(19).fill("none"),
    ] as Shadows,
    components: {
      MuiToolbar: {
        styleOverrides: {
          regular: {
            height: 72,
            minHeight: 72,
          },
        },
      },
      MuiButtonBase: {
        defaultProps: {
          // The props to apply
          disableRipple: false, // let the ripple live again 💣!
        },
      },
      MuiInput: {
        styleOverrides: {
          root: {
            height: 40,
          },
        },
      },
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            color: color.TextPrimary,
            fontWeight: 400,
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: color.TextSecondary,
              borderRadius: "4px",
            },
            "&::-webkit-scrollbar": {
              width: "9px",
              height: "7px",
              background: color.Gray,
              borderRadius: "4px",
            },
            "& *::-webkit-scrollbar-thumb": {
              backgroundColor: color.TextSecondary,
              borderRadius: "4px",
            },
            "& *::-webkit-scrollbar": {
              width: "7px",
              height: "7px",
              background: color.Gray,
              borderRadius: "4px",
            },
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            textTransform: "none",
            borderRadius: 50,
          },
        },
      },
      MuiDivider: {
        styleOverrides: {
          root: {
            borderColor: color.ContentWrapperBorder,
          },
        },
      },
    },
  };

  const customVariables = {
    colors: {
      textPrimary: color.TextPrimary,
      textSecondary: color.TextSecondary,
      textTertiary: color.TextTertiary,
      textDisabled: color.TextDisabled,
      brandPrimary: color.BrandPrimary,
      brandMedium: color.BrandMedium,
      brandLight: color.BrandLight,
      brandLighter: color.BrandLighter,
      brandLightBorder: color.BrandLightBorder,
      primaryHover: color.PrimaryHover,
      primaryDisabled: color.PrimaryDisabled,
      dangerPrimary: color.DangerPrimary,
      successPrimary: color.SuccessPrimary,
      border: color.Border,
      borderLight: color.BorderLight,
      backgroundLight: color.BackgroundLight,
      backgroundSecondary: color.BackgroundSecondary,
      backgroundTertiary: color.BackgroundTertiary,
      borderDisabled: color.BorderDisabled,
      gray: color.Gray,
      validation: color.Validation,
      darkGrey: color.DarkGrey,
      placeholder: color.Placeholder,
      fieldBackground: color.FieldBackground,
      primaryMainTransparent: `${color.White}1f`,
      contentWrapperBorder: color.ContentWrapperBorder,
      radioBtnDisabled: color.RadioBtnDisabled,
      workflowIcon: color.WorkflowIcon,
      white: color.White,
      chartPositive: color.ChartPositive,
      chartPositiveLight: color.ChartPositiveLight,
      chartPositiveLighter: color.ChartPositiveLighter,
      chartNeutral: color.ChartNeutral,
      chartNeutralLight: color.ChartNeutralLight,
      chartNeutralLighter: color.ChartNeutralLighter,
      chartNegative: color.ChartNegative,
    },
    shadow: {
      primary: "none",
      primaryHover: "0px 0px 15px 0px rgba(0, 0, 0, 0.15) inset",
    },
    borderRadius: "12px",
    vh: (val: number) => {
      return `calc(var(--vh, 1vh) * ${val})`;
    },
  };

  return createTheme(Object.assign(theme, customVariables)) as Theme;
};

// TODO enum;
export const getTheme = () => {
  return theme(colors());
};
